import React from "react";
import { ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { RouteComponentPropsHistory } from "../../types";

import { DateTime } from "luxon";

import DelayInput from "../DelayInput";
import TabControl from "../TabControl";

import { V1 } from "../../Api";
import { formatName, formatDateTimeLocalNumeric } from "../../Utilities";
import { IAuthProps, WellKnownGroups } from "../AuthContext";
import HasRight from "../HasRight";
import withAuth from "../withAuth";
import withTitle from "../withTitle";

import "../../table.css";
import "../viewer.css";

interface IAppointmentContactListViewState {
    appointments: any[];
    dateMode: "for" | "on";
    selectedTab: number;

    locations: any[];
    //searchText: string;
    //searchLocation: string;
    searchOffset: number;
    searchPerson: string;
    searchStatus: string;

    searchDate: string;

    isLoading: boolean | string;

    marketers: any[];
}

const PAGE_SIZE = 50;

class AppointmentContactListView extends React.Component<RouteComponentPropsHistory<any, any> & IAuthProps, IAppointmentContactListViewState> {
    constructor(props: Readonly<RouteComponentPropsHistory<any, any> & IAuthProps>) {
        super(props);

        const targetDate = DateTime.local().startOf("day").plus({ day: 1 });

        this.state = {
            appointments: [],
            dateMode: props.location.state ? props.location.state.dateMode : "for",
            selectedTab: props.location.state ? props.location.state.selectedTab : 1,

            locations: [],
            //searchLocation: props.location.state ? props.location.state.searchLocation : "",
            searchOffset: props.location.state && props.location.state.searchOffset ? props.location.state.searchOffset : 0,
            searchPerson: (props.authContext.hasRight("appt:readall")
                ? props.location.state && props.location.state.searchPerson
                    ? props.location.state.searchPerson
                    : props.authContext.isInGroup(WellKnownGroups.Marketer)
                        ? "caller"
                        : ""
                : "caller"),
            searchStatus: props.location.state ? props.location.state.searchStatus : "active_or_pending",
            //searchText: props.location.state ? props.location.state.searchText : "",

            searchDate: props.location.state ? props.location.state.searchDate : targetDate.toISODate(),

            isLoading: true,

            marketers: [],
        };

        this.handleElementChange = this.handleElementChange.bind(this);
        this.nextPage = this.nextPage.bind(this);
        this.previousPage = this.previousPage.bind(this);
        this.handleTabChanged = this.handleTabChanged.bind(this);
    }

    public async componentDidMount() {
        try {
            const marketers = await V1.get<any[]>(`employees/summary?group=${WellKnownGroups.Marketer}`);

            this.setState({
                marketers: marketers.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name)))
            });

            await this.search();
        }
        catch (e) {
            this.setState({
                isLoading: e.message || e || "Could not load data"
            });
        }
    }

    public render() {
        let table;

        if (this.state.isLoading === true) {
            table = <div className="spinner-center lds-dual-ring" />;
        }
        else if (typeof this.state.isLoading === "string") {
            table = <span className="error-text">{this.state.isLoading}</span>;
        }
        else {
            let tbody;

            if (this.state.appointments.length === 0) {
                tbody = (
                    <tr>
                        <td colSpan={10}>No appointments found</td>
                    </tr>
                );
            }
            else {
                tbody = this.state.appointments.map(a => (
                    <tr key={a.id} className={this.statusToClass(a.contactInfo.status)}>
                        <td className="inline" data-caption="Date: ">{formatDateTimeLocalNumeric(a.appointmentTimestamp)}</td>
                        <td className="inline wrap" data-caption="Name: ">{formatName(a.patient.name)}</td>
                        <td className="inline" data-caption="Location: ">{a.location.shortName || a.location.name}</td>
                        <td className="inline wrap" data-caption="Provider: ">{a.nurse ? formatName(a.nurse.name) : "-"}</td>
                        <td>
                            <Link className="button small" to={`/appointments/${a.id}`}>View</Link>
                        </td>
                    </tr>
                ));
            }

            table = (
                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th className="col-smallest">Scheduled For</th>
                            <th>Name</th>
                            <th className="col-smallest">Location</th>
                            <th>Provider</th>
                            <th className="col-smallest" />
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr>
                            <th>Appointments</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tbody}
                    </tbody>
                </table>
            );
        }

        return (
            <div className="viewer-root with-actionbar">
                <div className="filter-control-box">
                    <div className="control-group">
                        <span>Date:</span>
                        <DelayInput type="date" timeout={500} value={this.state.searchDate} name="searchDate" onChangeDelayed={this.handleElementChange} />
                    </div>
                    <span className="fill-space" />
                    {/*<select value={this.state.searchStatus} name="searchStatus" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All</option>
                        <option value="pending">Pending</option>
                        <option value="active">Active</option>
                        <option value="complete">Completed</option>
                        <option value="noshow">Soft-canceled</option>
                        <option value="canceled">Canceled</option>
                    </select>*/}
                    <select value={this.state.searchPerson} name="searchPerson" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <HasRight right="appt:readall">
                            <option value="">Everyone</option>
                        </HasRight>
                        <option value="caller">You</option>
                        <HasRight right="appt:readall">
                            {
                                this.state.marketers.map(a =>
                                    <option key={a.id} value={a.id}>{formatName(a.name)}</option>
                                )
                            }
                        </HasRight>
                    </select>
                </div>
                <TabControl headers={["Today", "1 day", "3 days", "4 days", "5 days", "7 days"]} defaultSelectedIndex={this.state.selectedTab} onTabChanged={this.handleTabChanged} />
                {table}
                &nbsp;
                <div className="action-panel action-panel-noflip">
                    <button className="button push-right text-with-icon" onClick={this.previousPage} disabled={this.state.searchOffset === 0}><i className="icon-angle-left" /><span>Previous</span></button>
                    <button className="button text-with-icon" onClick={this.nextPage} disabled={this.state.appointments.length < PAGE_SIZE}><span>Next</span><i className="icon-angle-right" /></button>
                </div>
            </div>
        );
    }

    private async search() {
        try {
            this.setState({
                isLoading: true
            });

            this.saveSearchState();

            const parameters: string[] = [
                `limit=${PAGE_SIZE}`,
                `offset=${this.state.searchOffset}`,
                "mode=contact",
                "sort=asc",
                `sort_by=date_for`
            ];
            //if (this.state.searchText) {
            //    parameters.push(`q=${this.state.searchText}`);
            //}
            //if (this.state.searchLocation) {
            //    parameters.push(`location=${this.state.searchLocation}`);
            //}
            if (this.state.searchPerson) {
                parameters.push(`person=${this.state.searchPerson}`);
            }
            if (this.state.searchStatus) {
                parameters.push(`status=${this.state.searchStatus}`);
            }
            if (this.state.searchDate) {
                const beforeDt = DateTime.fromISO(this.state.searchDate).endOf("day").plus({ millisecond: 1 });
                parameters.push(`after=${this.state.searchDate}`);
                parameters.push(`before=${beforeDt.toISODate()}`);
            }

            const results = await V1.get<any[]>(`appointments/summary?${parameters.join("&")}`);
            for (const item of results) {
                item.contactInfo = item.contactInfo || {};
            }

            this.setState({
                appointments: results,
                isLoading: false
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Failed to search"
            });
        }
    }

    private statusToClass(status: number) {
        switch (status) {
            case 2: return "row-green";
            case 1: return "row-yellow";
            default: return "";
        }
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            searchOffset: 0,

            [name]: value
        } as any, () => this.search());
    }

    private saveSearchState() {
        const location = this.props.location;
        location.state = {
            //searchLocation: this.state.searchLocation,
            searchOffset: this.state.searchOffset,
            searchPerson: this.state.searchPerson,
            searchStatus: this.state.searchStatus,
            //searchText: this.state.searchText,

            searchDate: this.state.searchDate,
            dateMode: this.state.dateMode,
            selectedTab: this.state.selectedTab
        };

        this.props.history.replace(location);
    }

    private nextPage() {
        this.setState(s => {
            return {
                searchOffset: s.searchOffset + PAGE_SIZE
            };
        }, () => this.search());
    }

    private previousPage() {
        this.setState(s => {
            return {
                searchOffset: Math.max(s.searchOffset - PAGE_SIZE, 0)
            };
        }, () => this.search());
    }

    private handleTabChanged(index: number) {
        let targetdate = DateTime.local().startOf("day");
        let offset = [0, 1, 3, 4, 5, 7][index];
        targetdate = targetdate.plus({ day: offset });

        this.setState({
            selectedTab: index,

            searchDate: targetdate.toISODate(),
        }, () => this.search());
    }
}

export default withAuth(withTitle("Upcoming Appointments", AppointmentContactListView));
